import { FC } from 'react';
import { Box, SxProps } from '@mui/material';
import { DataGrid as MuiDataGrid, GridColDef, useGridApiRef, GridColumnVisibilityModel } from '@mui/x-data-grid';

import { DataGridToolbar } from './DataGridToolbar';
import { DataGridActions, DataGridAction } from './DataGridAction';
import { useDataGrid } from '@carcareers/contexts';

interface DataGridFactoryProps {
  headerHeight?: number;
  fluidHeight?: boolean;
  sx?: SxProps;
}

interface DataGridProps {
  columns: GridColDef[];
  actions?: Partial<DataGridActions>;
  columnInitialState?: GridColumnVisibilityModel;
  alternateRowShading?: boolean;
  headerHeight?: number;
  fluidHeight?: boolean;
  sx?: SxProps;
}

export const DataGridFactory = <T = unknown,>(props?: DataGridFactoryProps) => {
  const Instance: FC<DataGridProps> = ({ columns, actions, columnInitialState, alternateRowShading = false, headerHeight = 195 }) => {
    const { items, loading, paginationModel, rowCount, onPaginationModelChange } = useDataGrid<T>();
    const apiRef = useGridApiRef();

    const enhancedColumns = [...columns];
    if (actions) {
      const actionColumn: GridColDef = {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        hideable: false,
        renderCell: (params) => {
          return <DataGridAction actions={actions} id={Number(params.id)} row={params.row} />;
        },
      };

      enhancedColumns.push(actionColumn);
    }

    return (
      <Box sx={{ width: '100%', px: 3, ...props?.sx }}>
        <DataGridToolbar apiRef={apiRef} actions={actions} />
        <MuiDataGrid
          data-testid="data-grid"
          apiRef={apiRef}
          rows={items}
          columns={enhancedColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={rowCount ?? 10}
          paginationMode="server"
          loading={loading}
          initialState={{
            columns: {
              columnVisibilityModel: columnInitialState,
            },
          }}
          disableRowSelectionOnClick
          sx={{
            height: props?.fluidHeight ? '100%' : `calc(100vh - ${props?.headerHeight ? props?.headerHeight + headerHeight : headerHeight}px)`,
            maxHeight: '100%',
            ...(alternateRowShading && {
              '& .MuiDataGrid-row:nth-child(odd)': {
                backgroundColor: 'grey.100',
              },
            }),
          }}
        />
      </Box>
    );
  };

  return Instance;
};

export default DataGridFactory;
